import { z } from 'zod'
import { activeListingActionSchema } from './listing-action'

/**
 * What a follow can point at: an organisation, its channel, a spot (`place`) or a listing.
 * Deliberately excludes users: a person-to-person connection is a friendship, which is
 * symmetric.
 */
export const followTargetTypeSchema = z.enum(['business', 'channel', 'place', 'listing'])

/**
 * The bell: on (`all`, pushes new events and posts) or off (`off`, no pushes). Every follow
 * starts off. `highlights` is a legacy value: sent by a client it turns the bell on, stored
 * on a row it stays silent.
 */
export const followNotifyLevelSchema = z.enum(['all', 'highlights', 'off'])

export const followTargetSchema = z.object({
  type: followTargetTypeSchema,
  id: z.string().uuid(),
})

export const followStateDtoSchema = z.object({
  following: z.boolean(),
  notifyLevel: followNotifyLevelSchema.nullable(),
  followerCount: z.number().int().nonnegative(),
})

export const followedTargetDtoSchema = z.object({
  type: followTargetTypeSchema,
  id: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  isVerified: z.boolean(),
  notifyLevel: followNotifyLevelSchema,
  followerCount: z.number().int().nonnegative(),
  /** Places only: "street, city" for the Favoriten row. */
  address: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  /** Places only: the action the place is running now. */
  activeAction: activeListingActionSchema.nullable().optional(),
})

export type FollowTargetType = z.infer<typeof followTargetTypeSchema>
export type FollowNotifyLevel = z.infer<typeof followNotifyLevelSchema>
export type FollowTarget = z.infer<typeof followTargetSchema>
export type FollowStateDto = z.infer<typeof followStateDtoSchema>
export type FollowedTargetDto = z.infer<typeof followedTargetDtoSchema>
